import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Toolbar from './components/Toolbar';
import PageHeader from './components/PageHeader';
import PageFooter from './components/PageFooter';
import PageContent from './components/PageContent';
import ResponsiveShadow from './components/ResponsiveShadow';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { formatDateView } from './utils/dateUtils';
import Pagination10 from './components/Pagination10';
import { formatString, truncateText } from './utils/StringUtils';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import usePagination10 from './components/hooks/usePagination10';
import LoadingModal from './components/modals/LoadingModal';
import Layout from './components/Layout';
import TableContainer from './components/TableContainer';
import TableHeader from './components/TableHeader';
import DfdModal from './components/modalServices/DfdModal';
import DfdItensModal from './components/modalServices/DfdItensModal';
import DfdLotesModal from './components/modalServices/DfdLotesModal';

function Dfds() {
  const [dfds, setDfds] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [isLoading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedDfd, setSelectedDfd] = useState(null);
  const [showDfdModal, setShowDfdModal] = useState(false);
  const [editingDfd, setEditingDfd] = useState(null);
  const [showItensModal, setShowItensModal] = useState(false);
  const [showLotesModal, setShowLotesModal] = useState(false);
  const [dfdParaModal, setDfdParaModal] = useState(null);
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase = localStorage.getItem('anoBase');
  const navigate = useNavigate();
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const [filtroTramitacao, setFiltroTramitacao] = useState('TODOS');
  // O valor de agrupamento agora vem do selectedDfd, se existir
  const agrupamento = selectedDfd?.agrupamento || 'ITEM';
  const dfdsFiltrados =
    filtroTramitacao === 'TODOS'
      ? dfds
      : dfds.filter(item => item.tramitacao === filtroTramitacao);

  const totalItems = dfdsFiltrados.length;
  const { setPagination } = usePagination10(totalItems);

  const selectedDfds = dfdsFiltrados.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  // Limpar seleção quando o filtro ou paginação mudar
  useEffect(() => {
    if (selectedDfd && !selectedDfds.find(dfd => dfd.num === selectedDfd.num)) {
      setSelectedDfd(null);
    }
  }, [selectedDfds, selectedDfd]);

  // Removido radio, seleção agora é feita por clique na linha

  const handleRowClick = dfd => {
    setSelectedDfd(selectedDfd && selectedDfd.num === dfd.num ? null : dfd);
  };

  // Funcionalidade de teclado simplificada
  useEffect(() => {
    const handleKeyDown = event => {
      if (
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.tagName === 'SELECT'
      ) {
        return; // Não interceptar se estiver em um campo de entrada
      }

      switch (event.key) {
        case 'Escape':
          setSelectedDfd(null);
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (selectedDfd) {
            const currentIndex = selectedDfds.findIndex(
              dfd => dfd.num === selectedDfd.num
            );
            if (currentIndex < selectedDfds.length - 1) {
              setSelectedDfd(selectedDfds[currentIndex + 1]);
            }
          } else if (selectedDfds.length > 0) {
            setSelectedDfd(selectedDfds[0]);
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (selectedDfd) {
            const currentIndex = selectedDfds.findIndex(
              dfd => dfd.num === selectedDfd.num
            );
            if (currentIndex > 0) {
              setSelectedDfd(selectedDfds[currentIndex - 1]);
            }
          } else if (selectedDfds.length > 0) {
            setSelectedDfd(selectedDfds[selectedDfds.length - 1]);
          }
          break;
        case ' ':
          event.preventDefault();
          if (selectedDfd) {
            // Para radio, espaço apenas confirma a seleção atual
            event.preventDefault();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedDfd, selectedDfds]);

  const toggleExpand = (rowKey) => {
    setExpandedRows(prev => ({ ...prev, [rowKey]: !prev[rowKey] }));
  };

  const loadDfds = () => {
    setLoading(true);
    axios
      .get(`${API_BASE_URL.DFD}/cnpj`, {
        params: {
          cnpj: cnpj,
          anoBase: anoBase,
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setDfds(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter listas:', error);
        navigate('/');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadDfds();
  }, [cnpj, anoBase, token]);

  const handleOpenCreateModal = () => {
    setEditingDfd(null);
    setShowDfdModal(true);
  };

  const handleOpenEditModal = dfd => {
    setEditingDfd(dfd);
    setShowDfdModal(true);
  };

  const handleCloseDfdModal = () => {
    setShowDfdModal(false);
    setEditingDfd(null);
  };

  const handleDfdSaved = () => {
    handleCloseDfdModal();
    setSelectedDfd(null);
    loadDfds();
  };

  if (!dfds) {
    return <div>Loading...</div>;
  }

  // Função para calcular total de itens nos lotes
  const getTotalItemsInLotes = (dfdLoteSet) => {
    if (!dfdLoteSet || dfdLoteSet.length === 0) return 0;
    return dfdLoteSet.reduce((total, lote) => {
      return total + (lote.dfdItemSet?.length || 0);
    }, 0);
  };

  function handleInformation(dfdToAprove) {
    axios
      .get(`${API_BASE_URL.TRAMITACAO}/justificativaDevolucao`, {
        params: {
          cnpjEntidade: dfdToAprove.cnpj,
          dfdNum: dfdToAprove.num,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        if (result.data) {
          const justificativa = result.data.justificativa;
          showDefaultModal(
            'information',
            `**Justificativa da devolução:**\n ${justificativa}`
          );
        } else {
        }
      })
      .catch(error => {
        console.error('Erro ao obter justificativa:', error);
        showDefaultModal('error', 'Erro ao obter justificativa!');
      });
  }
  function handleSend(dfdToAprove) {
    showConfirmModal('sendAprovacao', () => handleSendConfirm(dfdToAprove));
  }

  function handleSendConfirm(dfdToAprove) {
    if (dfdToAprove) {
      axios
        .get(`${API_BASE_URL.DFD_ITEM}/validate`, {
          params: {
            cnpj: dfdToAprove.cnpj,
            num: dfdToAprove.num,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(result => {
          if (result.data) {
            if (
              !dfdToAprove.dataCriacao ||
              !dfdToAprove.dataContratacao ||
              !dfdToAprove.tipo ||
              !dfdToAprove.requisitante ||
              !dfdToAprove.objeto ||
              !dfdToAprove.tipoDemanda ||
              !dfdToAprove.regulamentacao
            ) {
              const missingFields = [];
              if (!dfdToAprove.dataCriacao)
                missingFields.push('Data de Criação');
              if (!dfdToAprove.dataContratacao)
                missingFields.push('Data da Contratação');
              if (!dfdToAprove.tipo) missingFields.push('Tipo');
              if (!dfdToAprove.requisitante) missingFields.push('Requisitante');
              if (!dfdToAprove.objeto) missingFields.push('Objeto');
              if (!dfdToAprove.tipoDemanda)
                missingFields.push('Tipo de Demanda');
              if (!dfdToAprove.regulamentacao)
                missingFields.push('Regulamentação Municipal');

              const plural = missingFields.length > 1 ? 'os campos' : 'o campo';
              const fields = missingFields.join(', ');

              showDefaultModal(
                'error',
                `Por favor, preencha ${plural}: ${fields}.`
              );
              return;
            }
            const today = new Date().toISOString().split('T')[0]; // yyyy-mm-dd
            const updatedFormData = {
              ...dfdToAprove,
              tramitacao: 'ENVIADO PARA APROVAÇÃO',
              dataConclusaoDfd: today,
            };
            axios
              .post(`${API_BASE_URL.DFD}/updateTramitacao`, updatedFormData, {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              })
              .then(result => {
                const returned = result.data || {};
                setDfds(prevState =>
                  prevState.map(item =>
                    item.num === dfdToAprove.num
                      ? {
                          ...item,
                          tramitacao: returned.tramitacao || 'ENVIADO PARA APROVAÇÃO',
                          dataConclusaoDfd: returned.dataConclusaoDfd || today,
                        }
                      : item
                  )
                );
                showDefaultModal('success', 'O registro foi enviado com sucesso');
              })
              .catch(error => {
                console.error(
                  'Erro ao enviar o registro para aprovação:',
                  error
                );
                showDefaultModal(
                  'error',
                  'Erro ao enviar o registro para aprovação!'
                );
              });
          } else {
            showDefaultModal(
              'error',
              'Não é possível enviar para aprovação. Existem itens que precisam de atenção como categoria, unidade, quantidade e valor unitário!'
            );
          }
        })
        .catch(error => {
          console.error(
            'Não é possível enviar para aprovação. Existem itens que precisam de atenção como categoria, unidade, quantidade e valor unitário:',
            error
          );
          showDefaultModal(
            'error',
            'Não é possível enviar para aprovação. Existem itens que precisam de atenção como categoria, unidade, quantidade e valor unitário!'
          );
        });
    }
  }

  function handleDelete(dfdToDelete) {
    showConfirmModal('delete', () => handleDeleteConfirm(dfdToDelete));
  }

  function handleDeleteConfirm(dfdToDelete) {
    if (dfdToDelete) {
      axios
        .get(`${API_BASE_URL.DFD_ITEM}/exists`, {
          params: { cnpj: dfdToDelete.cnpj, num: dfdToDelete.num },
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(result => {
          if (result.data.exists) {
            showDefaultModal(
              'error',
              'Este registro possui vínculos e não pode ser excluído. Exclua os vínculos e tente novamente.'
            );
          } else {
            axios
              .delete(`${API_BASE_URL.DFD}/delete`, {
                params: {
                  cnpj: dfdToDelete.cnpj,
                  num: dfdToDelete.num,
                },
                headers: { Authorization: `Bearer ${token}` },
              })
              .then(result => {
                setDfds(prevState =>
                  prevState.filter(item => item.num !== dfdToDelete.num)
                );
                showDefaultModal(
                  'success',
                  'O registro foi excluído com sucesso'
                );
              })
              .catch(error => {
                console.error('Erro ao excluir o item:', error);
                showDefaultModal('error', 'Erro ao excluir o registro!');
              });
          }
        });
    }
  }

  async function handlePrint(dfd) {
    setIsExporting(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL.DFD}/preencherDfd`,
        dfd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          responseType: 'blob',
        }
      );
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `DFD ${formatString(dfd.num, 4)}.docx`;
      link.click();
    } catch (error) {
      console.error('Erro ao preencher o template:', error);
      showDefaultModal('error', 'Erro ao preencher o template.');
    } finally {
      setIsExporting(false);
    }
  }

  async function handlePrintPdf(dfd) {
    setIsExporting(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL.DFD}/preencherDfdPdf`,
        dfd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          responseType: 'blob',
        }
      );
      // response.data é um Blob (porque responseType: 'blob')
      const pdfBlob = response.data;
      const pdfUrl = window.URL.createObjectURL(pdfBlob);
      const tituloRelatorio = `DFD_${formatString(dfd.num, 4)}`;
      
      // Guardar blob em storage global para evitar garbage collection
      window.pdfStorage = window.pdfStorage || {};
      const pdfId = `pdf_${Date.now()}_${Math.random()}`;
      window.pdfStorage[pdfId] = pdfBlob;
      
      // Armazenar apenas referência e metadados em sessionStorage
      sessionStorage.setItem('pdfViewerData', JSON.stringify({
        title: tituloRelatorio,
        nomeArquivo: `${tituloRelatorio}.pdf`,
        pdfId: pdfId,
        url: pdfUrl
      }));
      
      // Disparar evento para atualizar o PdfViewerModal
      window.dispatchEvent(new CustomEvent('pdfViewerUpdate'));
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      showDefaultModal('error', 'Erro ao gerar PDF.');
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="DFDs Cadastrados" icon="bi-file-earmark-text" backButton={false} closeRoute="/home" />
        <PageContent style={{ paddingBottom: 0 }}>
          <Toolbar
            selectedId={selectedDfd}
            actions={[
              { key: 'novo', label: 'Novo', icon: 'bi-plus-circle', variant: 'success', onClick: handleOpenCreateModal },
              { key: 'editar', label: 'Editar', icon: 'bi-pencil-square', variant: 'primary', onClick: dfd => handleOpenEditModal(dfd), requiresSelection: true },
              { key: 'word', label: 'Word', icon: 'bi-file-word', variant: 'secondary', onClick: dfd => handlePrint(dfd), requiresSelection: true },
              { key: 'pdf', label: 'PDF', icon: 'bi-file-pdf', variant: 'secondary', onClick: dfd => handlePrintPdf(dfd), requiresSelection: true },
              { key: 'justificativa', label: 'Justificativa', icon: 'bi-info-circle', variant: 'warning', onClick: dfd => handleInformation(dfd), show: selectedDfd?.tramitacao === 'DEVOLVIDO PARA AJUSTES' },
              { key: 'excluir', label: 'Excluir', icon: 'bi-trash3', variant: 'danger', onClick: dfd => handleDelete(dfd), show: selectedDfd?.tramitacao === 'EM ELABORAÇÃO' },
              { key: 'enviar', label: 'Enviar para Aprovação', icon: 'bi-send', variant: 'success', onClick: dfd => handleSend(dfd), show: selectedDfd?.tramitacao === 'EM ELABORAÇÃO' || selectedDfd?.tramitacao === 'DEVOLVIDO PARA AJUSTES' },
              { key: 'itens', label: agrupamento === 'LOTE' ? 'Lotes' : 'Itens', icon: agrupamento === 'LOTE' ? 'bi-boxes' : 'bi-list-ul', variant: 'info', onClick: dfd => { setDfdParaModal(dfd); if ((dfd?.agrupamento || agrupamento) === 'LOTE') { setShowLotesModal(true); } else { setShowItensModal(true); } }, requiresSelection: true },
            ]}
            searchControls={(
              <div className="d-flex align-items-center small">
                <label className="visually-hidden">Filtro</label>
                  <select className="form-select form-select-sm filter-input-compact" style={{ width: '200px' }} value={filtroTramitacao} onChange={e => setFiltroTramitacao(e.target.value)}>
                  <option value="TODOS">Todos</option>
                  <option value="APROVADO">Aprovados</option>
                  <option value="DEVOLVIDO PARA AJUSTES">Devolvidos para ajustes</option>
                  <option value="EM ELABORAÇÃO">Em elaboração</option>
                  <option value="ENVIADO PARA APROVAÇÃO">Enviados para aprovação</option>
                  <option value="REPROVADO">Reprovados</option>
                </select>
              </div>
            )}
          />

          <LoadingModal loading={isLoading || isExporting} />
          <TableContainer className="table-responsive mb-2" tableClass="table-custom-radius">
        <TableHeader columns={[
          { label: 'Nº', width: '7%' },
          { label: 'Data da Contratação', width: '7%' },
          { label: 'Área Requisitante', width: '20%' },
          { label: 'Tipo', width: '10%' },
          { label: 'Agrupamento', width: '10%' },
          { label: 'Objeto' },
          { label: 'Tramitação', width: '15%' },
          { label: 'Qtde', width: '5%', align: 'center' },
        ]} />
        {/* <tbody className='small'> */}
        <tbody>
          {!isLoading && selectedDfds.length > 0 ? (
            selectedDfds.map(c => {
              const isActiveRow = selectedDfd && selectedDfd.num === c.num;
              return (
                <tr
                  key={`${c.cnpj}-${c.num}`}
                  className={isActiveRow ? 'table-primary fw-bold' : ''}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleRowClick(c)}
                  title={
                    isActiveRow ? 'DFD selecionado' : 'Clique para selecionar'
                  }
                >
                  <td style={{ width: '5%' }}>{formatString(c.num, 4)}</td>
                  <td style={{ width: '7%' }}>
                    {formatDateView(c.dataContratacao)}
                  </td>
                  <td style={{ width: '20%' }}>{c.requisitante}</td>
                  <td style={{ width: '10%' }}>{c.tipo}</td>
                  <td style={{ width: '10%' }}>{c.agrupamento}</td>
                  <td>
                    {(() => {
                      const rowKey = `${c.cnpj}-${c.num}`;
                      const desc = c.objeto || '';
                      const isExpanded = !!expandedRows[rowKey];
                      const limit = 80;
                      const shortDesc = desc.length > limit ? desc.slice(0, limit) + '...' : desc;
                      return (
                        <>
                          {desc.length > limit && (
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 me-1 align-middle"
                              style={{ verticalAlign: 'middle', color: '#000' }}
                              onClick={(e) => { e.stopPropagation(); toggleExpand(rowKey); }}
                              aria-expanded={isExpanded}
                              aria-label={isExpanded ? 'Recolher objeto' : 'Expandir objeto'}
                            >
                              <i className={`bi ${isExpanded ? 'bi-dash-circle' : 'bi-plus-circle'}`} aria-hidden="true"></i>
                            </button>
                          )}
                          <span style={{ whiteSpace: 'pre-wrap', display: 'inline' }}>{isExpanded ? desc : shortDesc}</span>
                        </>
                      );
                    })()}
                  </td>
                  <td style={{ width: '15%' }}>
                    <span
                      className={`badge ${
                        c.tramitacao === 'APROVADO'
                          ? 'bg-success'
                          : c.tramitacao === 'EM ELABORAÇÃO'
                          ? 'bg-secondary'
                          : c.tramitacao === 'ENVIADO PARA APROVAÇÃO'
                          ? 'bg-primary'
                          : c.tramitacao === 'DEVOLVIDO PARA AJUSTES'
                          ? 'bg-warning text-dark'
                          : c.tramitacao === 'REPROVADO'
                          ? 'bg-danger'
                          : 'bg-light text-dark'
                      }`}
                    >
                      {c.tramitacao}
                    </span>
                  </td>
                  <td className="text-center" style={{ width: '5%' }}>
                    {c.agrupamento === 'LOTE' 
                      ? (() => {
                          const numLotes = c.dfdLoteSet?.length || 0;
                          const numItens = getTotalItemsInLotes(c.dfdLoteSet);
                          return `${numLotes} ${numLotes === 1 ? 'lote' : 'lotes'} / ${numItens} ${numItens === 1 ? 'item' : 'itens'}`;
                        })()
                      : (() => {
                          const numItens = c.dfdItemSet?.length || 0;
                          return `${numItens} ${numItens === 1 ? 'item' : 'itens'}`;
                        })()}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="8" className="text-center">
                Nenhum registro encontrado.
              </td>
            </tr>
          )}
        </tbody>
      </TableContainer>
        </PageContent>
        <PageFooter>
          <Pagination10 {...setPagination} />
        </PageFooter>
      </ResponsiveShadow>
      <DfdModal isOpen={showDfdModal} onClose={handleCloseDfdModal} initialDfd={editingDfd} onSaved={handleDfdSaved} />
      <DfdItensModal isOpen={showItensModal} onClose={() => setShowItensModal(false)} dfd={dfdParaModal} />
      <DfdLotesModal isOpen={showLotesModal} onClose={() => setShowLotesModal(false)} dfd={dfdParaModal} />
          <DefaultModal {...setDefaultModal} />
          <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}

export default Dfds;
